import { fromPromise, setup, assign } from "xstate-5";
import { createSharedMachine, xlog } from "vue-app/utils/xstate";
import { AxiosError, isAxiosError } from "axios";
import { CHANNEL_TYPE } from "vue-app/types/enums";
import { SMSCredit } from "vue-app/components/SMS/SMS.types";
import {
  DTOSmsSubscriptionResponse,
  DTOSmsPredictedAudienceResponse,
  DTOPriceTiersResponse,
  DTOPromotionCodeResponse,
  DTOWhatsappPredictedAudienceResponse,
  DTOWhatsappSubscriptionResponse,
  DTOWhatsappBillingLimitV2DynamoDbEntity,
  DTOTierPrice,
} from "vue-app/api/dto.d";
import {
  getSMSPackages,
  getSMSCredits,
  getSMSStatistics,
  getSMSSubscription,
  postSMSSubscription,
  deleteSMSSubscription,
} from "vue-app/api/sms/sms";
import { ChannelLoader } from "vue-app/components/PackageManager/PackageManager.types";
import {
  getPackages,
  getCredits,
  getSubscription,
  getPredictedAudience,
  postSubscription,
  deleteSubscription,
  hasFreePackageClaimed,
} from "vue-app/api/WhatsappService";
import {
  getPackage,
  getTotalCredits,
  roundUpToNearest10AfterMultiplyingBy3,
} from "vue-app/components/PackageManager/PackageManager.helpers";
import { getValidatePromotionCode } from "vue-app/api/StripeService";
import { useSharedMachine } from "vue-app/composables/useSharedMachine";

type SMSChannelLoader = ChannelLoader<DTOSmsPredictedAudienceResponse, SMSCredit[], DTOSmsSubscriptionResponse>;
type WhatsAppChannelLoader = ChannelLoader<
  DTOWhatsappPredictedAudienceResponse,
  DTOWhatsappBillingLimitV2DynamoDbEntity[],
  DTOWhatsappSubscriptionResponse
>;

const lang = window.EDRONE_CONTEXT.user.lang || "en";
const channelDataLoaders: Partial<Record<CHANNEL_TYPE, SMSChannelLoader | WhatsAppChannelLoader>> = {
  [CHANNEL_TYPE.SMS]: {
    getPackages: getSMSPackages,
    getCredits: getSMSCredits,
    getStatistics: getSMSStatistics,
    getSubscription: getSMSSubscription,
    saveSubscription: postSMSSubscription,
    deleteSubscription: deleteSMSSubscription,
    helpUrl: `https://help.edrone.me/${lang}/articles/11058668`,
    integrationUrl: ``,
    isIntegrated: true,
    renewalDate: (subscription: DTOSmsSubscriptionResponse) => {
      if (subscription && subscription.renewalDate) {
        return subscription.renewalDate;
      }
      return 0;
    },
    calculateCustomPackageSize: (
      statistics: DTOSmsPredictedAudienceResponse,
      packages: DTOPriceTiersResponse,
      subscription: DTOSmsSubscriptionResponse
    ) => {
      return (
        subscription.packageSize ||
        roundUpToNearest10AfterMultiplyingBy3(
          statistics.totalPredictedSmsAudienceCount,
          packages.tierPrices[packages.tierPrices.length - 1].fromUnits
        )
      );
    },
    calculateRecomendedPackageSize: (statistics: DTOSmsPredictedAudienceResponse, packages: DTOPriceTiersResponse) => {
      if (!statistics.totalPredictedSmsAudienceCount || !packages.tierPrices) {
        return 0;
      }

      return roundUpToNearest10AfterMultiplyingBy3(
        statistics.totalPredictedSmsAudienceCount,
        packages.tierPrices[packages.tierPrices.length - 1].fromUnits
      );
    },
    getTotalCredits: (credits: SMSCredit[]) => {
      return getTotalCredits(credits, "smsCredits");
    },
  } satisfies SMSChannelLoader,
  [CHANNEL_TYPE.WHATSAPP]: {
    getPackages,
    getCredits,
    getSubscription,
    getStatistics: getPredictedAudience,
    saveSubscription: postSubscription,
    deleteSubscription,
    helpUrl: `https://help.edrone.me/${lang}/articles/12033042`,
    integrationUrl: `/app/integrations/whatsapp`,
    isIntegrated: window.EDRONE_CONTEXT.whatsappIntegrated,
    renewalDate: (subscription: DTOWhatsappSubscriptionResponse) => {
      if (subscription && subscription.renewalDate) {
        return subscription.renewalDate;
      }
      return null;
    },
    calculateCustomPackageSize: (
      statistics: DTOWhatsappPredictedAudienceResponse,
      packages: DTOPriceTiersResponse,
      subscription: DTOWhatsappSubscriptionResponse
    ) => {
      return (
        subscription.packageSize ||
        roundUpToNearest10AfterMultiplyingBy3(
          statistics.totalPredictedWhatsappAudienceCount,
          packages.tierPrices[packages.tierPrices.length - 1].fromUnits
        )
      );
    },
    calculateRecomendedPackageSize: (
      statistics: DTOWhatsappPredictedAudienceResponse,
      packages: DTOPriceTiersResponse
    ) => {
      if (!statistics.totalPredictedWhatsappAudienceCount || !packages.tierPrices) {
        return 0;
      }

      return roundUpToNearest10AfterMultiplyingBy3(
        statistics.totalPredictedWhatsappAudienceCount,
        packages.tierPrices[packages.tierPrices.length - 1].fromUnits
      );
    },
    getTotalCredits: (credits: DTOWhatsappBillingLimitV2DynamoDbEntity[]) => {
      return getTotalCredits(credits, "whatsappCredits");
    },
  } satisfies WhatsAppChannelLoader,
};

const getChannelLoader = (channelType: CHANNEL_TYPE): SMSChannelLoader | WhatsAppChannelLoader => {
  const loader = channelDataLoaders[channelType];
  if (!loader) {
    throw new Error(`Unsupported channel type: ${channelType}`);
  }
  return loader;
};

interface PackageData {
  packages: DTOPriceTiersResponse;
  credits: SMSCredit[] | DTOWhatsappBillingLimitV2DynamoDbEntity[];
  statistics: DTOSmsPredictedAudienceResponse | DTOWhatsappPredictedAudienceResponse;
  subscription: DTOSmsSubscriptionResponse | DTOWhatsappSubscriptionResponse;
  selectedPackage: {
    price: number;
    fromUnits: number | null;
    upToUnits: number | null;
  };
  customPackageSize: number;
  recommendedPackage: DTOTierPrice | null;
  coupon: DTOPromotionCodeResponse | null;
  recommendedPackageSize: number;
  totalCredits: number;
  renewalDate: number | null;
  freePackageClaimed: boolean;
}

interface MachineContext {
  data: PackageData;
  helpUrl: string;
  error: Error | null;
  couponCode: string;
  channelTypeId: CHANNEL_TYPE;
  isIntegrated: boolean;
  integrationUrl: string;
}

interface MachineInput {
  channelTypeId: CHANNEL_TYPE;
}

export const machine = setup({
  types: {
    tags: {} as
      | "idle"
      | "loading"
      | "validatingCoupon"
      | "showingPricing"
      | "integrationRequired"
      | "confirmingPackage"
      | "updatingPackage"
      | "assasRequested"
      | "confirmCancelSubscription"
      | "removingSubscription"
      | "error",
    events: {} as
      | { type: "LOAD_DATA" }
      | { type: "CHANGE_PACKAGE_SIZE"; size: number }
      | { type: "SHOW_PRICING" }
      | { type: "APPLY_COUPON"; coupon: string }
      | { type: "UPDATE_COUPON_CODE"; couponCode: string }
      | { type: "RESET_COUPON" }
      | { type: "CONFIRM_UPGRADE" }
      | { type: "SAVE_PACKAGE" }
      | { type: "CANCEL_SUBSCRIPTION" }
      | { type: "CONFIRM" }
      | { type: "CANCEL" }
      | { type: "CLOSE" }
      | { type: "RETRY" }
      | {
          type: "xstate.done.actor.loadPackageData";
          output: { data: PackageData; helpUrl: string; isIntegrated: boolean; integrationUrl: string };
        }
      | { type: "xstate.error.actor.loadPackageData"; error: AxiosError }
      | { type: "xstate.done.actor.validateCoupon"; output: DTOPromotionCodeResponse }
      | { type: "xstate.error.actor.validateCoupon"; error: AxiosError }
      | { type: "xstate.done.actor.savePackage"; output: { paymentLink?: string } }
      | { type: "xstate.error.actor.savePackage"; error: AxiosError }
      | { type: "xstate.done.actor.removeSubscription"; output: { success: boolean } }
      | { type: "xstate.error.actor.removeSubscription"; error: AxiosError },
    context: {} as MachineContext,
    input: {} as MachineInput,
  },
  actors: {
    loadPackageData: fromPromise(async ({ input }: { input: { channelTypeId: CHANNEL_TYPE } }) => {
      xlog({ label: "loadPackageData" });

      const loader = getChannelLoader(input.channelTypeId);
      const [packages, credits, statistics, subscription] = await Promise.all([
        loader.getPackages(),
        loader.getCredits(),
        loader.getStatistics(),
        loader.getSubscription(),
      ]);

      let customPackageSize: number;
      let recommendedPackageSize: number;
      let totalCredits: number;
      let renewalDate: number | null;
      let freePackageClaimed: boolean = true;

      if (input.channelTypeId === CHANNEL_TYPE.SMS) {
        const smsLoader = loader as SMSChannelLoader;
        customPackageSize = smsLoader.calculateCustomPackageSize(
          statistics as DTOSmsPredictedAudienceResponse,
          packages,
          subscription as DTOSmsSubscriptionResponse
        );
        recommendedPackageSize = smsLoader.calculateRecomendedPackageSize(
          statistics as DTOSmsPredictedAudienceResponse,
          packages
        );
        totalCredits = smsLoader.getTotalCredits(credits as SMSCredit[]);
        renewalDate = smsLoader.renewalDate(subscription as DTOSmsSubscriptionResponse);
      } else {
        const whatsappLoader = loader as WhatsAppChannelLoader;
        customPackageSize = whatsappLoader.calculateCustomPackageSize(
          statistics as DTOWhatsappPredictedAudienceResponse,
          packages,
          subscription as DTOWhatsappSubscriptionResponse
        );
        recommendedPackageSize = whatsappLoader.calculateRecomendedPackageSize(
          statistics as DTOWhatsappPredictedAudienceResponse,
          packages
        );
        totalCredits = whatsappLoader.getTotalCredits(credits as DTOWhatsappBillingLimitV2DynamoDbEntity[]);
        renewalDate = whatsappLoader.renewalDate(subscription as DTOWhatsappSubscriptionResponse);
        freePackageClaimed = await hasFreePackageClaimed();
      }

      const data: PackageData = {
        packages,
        credits,
        statistics,
        subscription,
        selectedPackage: {
          price: 0,
          fromUnits: null,
          upToUnits: null,
        },
        recommendedPackage: recommendedPackageSize ? getPackage(recommendedPackageSize, packages.tierPrices) : null,
        customPackageSize,
        recommendedPackageSize,
        totalCredits,
        coupon: null,
        renewalDate,
        freePackageClaimed,
      };

      const selected = getPackage(data.customPackageSize, packages.tierPrices);
      data.selectedPackage = selected || data.selectedPackage;

      const helpUrl = loader.helpUrl;
      const isIntegrated = loader.isIntegrated;
      const integrationUrl = loader.integrationUrl;

      return { data, helpUrl, isIntegrated, integrationUrl };
    }),
    validateCoupon: fromPromise(async ({ input }: { input: { couponCode: string; channelTypeId: CHANNEL_TYPE } }) => {
      xlog({ label: "validateCoupon" });
      return await getValidatePromotionCode(input.couponCode);
    }),
    savePackage: fromPromise(
      async ({
        input,
      }: {
        input: {
          channelTypeId: CHANNEL_TYPE;
          packageSize: number;
          promotionCode?: string;
        };
      }) => {
        xlog({ label: "savePackage" });
        const loader = getChannelLoader(input.channelTypeId);
        const { packageSize, promotionCode } = input;
        return await loader.saveSubscription(packageSize, promotionCode);
      }
    ),
    removeSubscription: fromPromise(async ({ input }: { input: { channelTypeId: CHANNEL_TYPE } }) => {
      xlog({ label: "removeSubscription" });
      const loader = getChannelLoader(input.channelTypeId);
      return await loader.deleteSubscription();
    }),
  },
}).createMachine({
  id: "packageManagerMachine",
  initial: "loading",
  context: ({ input }) => ({
    data: {
      packages: { currency: null, tierPrices: [] },
      credits: [],
      statistics: {} as PackageData["statistics"],
      subscription: {} as DTOSmsSubscriptionResponse,
      selectedPackage: {
        price: 0,
        fromUnits: null,
        upToUnits: null,
      },
      customPackageSize: 0,
      recommendedPackageSize: 0,
      recommendedPackage: null,
      totalCredits: 0,
      coupon: null,
      renewalDate: null,
      freePackageClaimed: true,
    },
    helpUrl: "",
    integrationUrl: "",
    isIntegrated: false,
    error: null,
    couponCode: "",
    channelTypeId: input.channelTypeId,
  }),
  states: {
    idle: {
      tags: "idle",
      on: {
        LOAD_DATA: "loading",
        CHANGE_PACKAGE_SIZE: {
          actions: assign(({ event, context }) => {
            if (context.data && context.data.packages) {
              const selectedPackage = getPackage(event.size, context.data.packages.tierPrices || []);
              return {
                data: {
                  ...context.data,
                  customPackageSize: event.size,
                  selectedPackage: selectedPackage
                    ? {
                        price: selectedPackage.price,
                        fromUnits: selectedPackage.fromUnits,
                        upToUnits: selectedPackage.upToUnits,
                      }
                    : context.data.selectedPackage,
                },
              };
            }
            return {};
          }),
        },
        APPLY_COUPON: {
          target: "validatingCoupon",
          actions: assign(({ event }) => ({
            couponCode: event.coupon,
          })),
        },
        UPDATE_COUPON_CODE: {
          actions: assign(({ event }) => ({
            couponCode: event.couponCode,
          })),
        },
        RESET_COUPON: {
          actions: assign(({ context }) => ({
            couponCode: "",
            data: {
              ...context.data,
              coupon: null,
            },
          })),
        },
        CONFIRM_UPGRADE: [
          {
            target: "integrationRequired",
            guard: ({ context }) => {
              return !context.isIntegrated;
            },
          },
          {
            target: "confirmingPackage",
          },
        ],
        SHOW_PRICING: "showingPricing",
        CANCEL_SUBSCRIPTION: "confirmCancelSubscription",
      },
    },

    loading: {
      tags: "loading",
      invoke: {
        src: "loadPackageData",
        input: ({ context }) => ({ channelTypeId: context.channelTypeId }),
        onDone: {
          target: "idle",
          actions: assign(({ event }) => ({
            data: event.output.data,
            helpUrl: event.output.helpUrl,
            integrationUrl: event.output.integrationUrl,
            isIntegrated: event.output.isIntegrated,
            error: null,
          })),
        },
        onError: {
          target: "error",
          actions: assign(({ event }) => ({
            error: event.error as Error,
          })),
        },
      },
    },

    validatingCoupon: {
      tags: "validatingCoupon",
      invoke: {
        src: "validateCoupon",
        input: ({ context }) => ({
          couponCode: context.couponCode || "",
          channelTypeId: context.channelTypeId,
        }),
        onDone: {
          target: "idle",
          actions: assign(({ event, context }) => ({
            data: {
              ...context.data,
              coupon: event.output,
            },
            error: null,
          })),
        },
        onError: {
          target: "idle",
          actions: assign(({ context }) => ({
            data: {
              ...context.data,
              coupon: null,
            },
            error: null,
          })),
        },
      },
    },

    showingPricing: {
      tags: "showingPricing",
      on: {
        CANCEL: "idle",
      },
    },

    integrationRequired: {
      tags: "integrationRequired",
      on: {
        CANCEL: "idle",
      },
    },

    confirmingPackage: {
      tags: "confirmingPackage",
      on: {
        SAVE_PACKAGE: "updatingPackage",
        CANCEL: "idle",
      },
    },

    updatingPackage: {
      tags: "updatingPackage",
      invoke: {
        src: "savePackage",
        input: ({ context }) => ({
          packageSize: context.data.customPackageSize || 0,
          promotionCode: context.couponCode,
          channelTypeId: context.channelTypeId,
        }),
        onDone: {
          target: "idle",
          actions: ({ event }) => {
            if (event.output.paymentLink) {
              window.open(event.output.paymentLink, "_self");
            }
          },
        },
        onError: {
          target: "idle",
          actions: [
            assign(({ event }) => ({
              error: event.error as Error,
            })),
            ({ event }) => {
              if (isAxiosError(event.error)) {
                if (event.error.response?.data.message.includes("Support agents cannot create SMS subscriptions")) {
                  window.showToast(window._trans._billing.errors.supportAgent, "warn");
                } else if (event.error.response?.data.message.includes("Billing customer not found for trackerId")) {
                  window.showToast(window._trans._billing.errors.billingCustomerNotFound, "warn");
                } else {
                  window.showToast(window._trans._billing.errors.internalServerError, "error");
                }
              }
            },
          ],
        },
      },
    },

    assasRequested: {
      tags: "assasRequested",
      on: {
        CANCEL: "idle",
      },
    },

    confirmCancelSubscription: {
      tags: "confirmCancelSubscription",
      on: {
        CANCEL: "idle",
        CONFIRM: "removingSubscription",
      },
    },

    removingSubscription: {
      tags: "removingSubscription",
      invoke: {
        src: "removeSubscription",
        input: ({ context }) => ({ channelTypeId: context.channelTypeId }),
        onDone: {
          target: "loading",
          actions: assign(() => ({
            error: null,
          })),
        },
        onError: {
          target: "idle",
          actions: assign(({ event }) => ({
            error: event.error as Error,
          })),
        },
      },
    },

    error: {
      tags: "error",
      on: {
        RETRY: "loading",
        CANCEL: "idle",
      },
    },
  },
});

const sharedMachines = new Map<CHANNEL_TYPE, ReturnType<typeof createSharedMachine<typeof machine>>>();
export const getPackageManagerMachine = (channelType: CHANNEL_TYPE) => {
  if (!sharedMachines.has(channelType)) {
    sharedMachines.set(channelType, createSharedMachine<typeof machine>());
  }
  return sharedMachines.get(channelType)!;
};

export const usePackageManagerMachine = (channelType: CHANNEL_TYPE) => {
  const sharedMachine = getPackageManagerMachine(channelType);
  return useSharedMachine(sharedMachine, machine, { channelTypeId: channelType });
};
